import _ from 'lodash';
import { TextFragment } from '@scale/shared/types/ocr';

const MAX_LOOKBACK_TOKENS = 3;
const MIN_SUFFIX_LENGTH = 8;

interface SearchOptions {
  skipTokens?: Set<string>;
  shortTokenLength?: number;
}

export enum BoxRender {
  Highlight = 'highlight',
  PossibleMatch = 'possibleMatch',
}

export interface BoundingBox {
  render: BoxRender;
  top: number;
  left: number;
  width: number;
  height: number;
}

interface SearchEntry {
  id: string;
  text: string;
  tokens: SearchToken[];
  boundingBox?: BoundingBox;
}

export class SearchIndex {
  private index: Map<string, SearchEntry[]>;
  private options: SearchOptions;

  constructor(options?: SearchOptions) {
    this.index = new Map();
    this.options = options || {};
  }

  public add(textFragment: TextFragment): void {
    const textTrim = textFragment.text.trim();
    const tokens = getSearchTokens(textTrim);
    const id = Math.random().toString(16).slice(2);
    for (let i = 0; i < tokens.length - 1; i++) {
      const token = tokens[i];
      const newEntryText = textTrim.slice(token.start);
      if (newEntryText.length < MIN_SUFFIX_LENGTH) {
        continue;
      }
      const entries = this.index.get(token.value) || [];
      const isDuplicate = entries.some(entry => {
        return entry.text === newEntryText;
      });
      if (isDuplicate) {
        continue;
      }
      const newEntry: SearchEntry = {
        id,
        text: newEntryText,
        tokens: tokens.slice(i),
      };
      if (textFragment.boundingBox) {
        newEntry.boundingBox = {
          render: BoxRender.PossibleMatch,
          top: textFragment.boundingBox.Top,
          left: textFragment.boundingBox.Left,
          width: textFragment.boundingBox.Width,
          height: textFragment.boundingBox.Height,
        };
      }
      entries.push(newEntry);
      this.index.set(token.value, entries);
    }
  }

  public search(prefix: string): SearchResult[] {
    const tokens = getSearchTokens(prefix);
    if (!tokens.length) {
      return [];
    }
    // Take the last 3 tokens. Prioritize entries that match on multiple tokens.
    const isCompleteFinalToken = prefix.endsWith(' ');
    const lastFewTokens = tokens.slice(Math.max(tokens.length - MAX_LOOKBACK_TOKENS, 0));
    const duplicates = new Set<string>();
    const results = _.flatMap(lastFewTokens, (token, index) => {
      if (this.options.skipTokens && this.options.skipTokens.has(token.value)) {
        return [];
      }
      const entries = this.index.get(token.value);
      if (!entries) {
        return [];
      }
      const isLastIndex = index === lastFewTokens.length - 1;
      if (this.options.shortTokenLength) {
        const isShortToken = token.value.length <= this.options.shortTokenLength;
        if (!isCompleteFinalToken && isShortToken && isLastIndex) {
          return [];
        }
      }
      const searchTokens = lastFewTokens.slice(index);
      // Last index will always match because it is the lookup term. Token matching
      // will accept prefix match only for last token.
      const matchingEntries = entries.filter(entry => {
        return isLastIndex || everyTokenMatches(searchTokens, entry.tokens);
      });
      // Skip duplicates for higher priority previous match tokens.
      const uniqueMatchingEntries = matchingEntries.filter(entry => {
        const exists = duplicates.has(entry.id);
        duplicates.add(entry.id);
        return !exists;
      });
      const searchResults = uniqueMatchingEntries.map(entry => {
        const searchResult: SearchResult = {
          text: entry.text,
          start: token.start,
          boundingBox: entry.boundingBox,
        };
        return searchResult;
      });
      return searchResults;
    });
    return _.sortBy(results, result => -(result.text.length - result.start));
  }
}

export interface SearchResult {
  start: number;
  text: string;
  boundingBox?: BoundingBox;
}

export function everyTokenMatches(
  searchTokens: SearchToken[],
  entryTokens: SearchToken[],
): boolean {
  if (searchTokens.length > entryTokens.length) {
    return false;
  }
  return searchTokens.every((token, index) => {
    if (index === searchTokens.length - 1) {
      return entryTokens[index].value.startsWith(token.value);
    }
    return entryTokens[index].value === token.value;
  });
}

interface SearchToken {
  value: string;
  start: number;
  end: number;
}

const allQuoteChars = [
  '\\u0022',
  '\\u0027',
  '\\u0060',
  '\\u00B4',
  '\\u2018',
  '\\u2019',
  '\\u201C',
  '\\u201D',
];

function stripPunctuation(text: string): string {
  const regExp = new RegExp('[' + allQuoteChars.join('') + ']', 'g');
  const token = text.replace(regExp, '');
  return _.trim(token, '.-;');
}

export function getSearchTokens(text: string): SearchToken[] {
  const tokens: SearchToken[] = [];
  const wordRegExp = new RegExp(`[\\w${allQuoteChars.join('')}\\-.]+`, 'gm');
  let match: RegExpExecArray | null = null;
  while ((match = wordRegExp.exec(text))) {
    const start = match.index;
    const end = wordRegExp.lastIndex;
    const value = stripPunctuation(text.slice(start, end).toLowerCase());
    if (['', '-', '.'].includes(value)) {
      continue;
    }
    const token: SearchToken = {
      value,
      start,
      end,
    };
    tokens.push(token);
  }
  return tokens;
}

export function getBoxStyle(bbox: BoundingBox): React.CSSProperties {
  return {
    top: getPctCSS(bbox.top),
    left: getPctCSS(bbox.left),
    width: getPctCSS(bbox.width),
    height: getPctCSS(bbox.height),
  };
}

function getPctCSS(pct: number): string {
  return (pct * 100).toFixed(4) + '%';
}

export class HighlightIndex {
  private index: Map<string, TextFragment[]>;
  private clusters: Map<string, TextFragment[]>;

  constructor() {
    // Index fragment by token value.
    this.index = new Map();
    // Index fragment by parent to cluster words.
    this.clusters = new Map();
  }

  public add(fragment: TextFragment): void {
    const tokens = getSearchTokens(fragment.text);
    if (tokens.length !== 1) {
      return;
    }
    const key = tokens[0].value;
    const fragments = this.index.get(key) || [];
    fragments.push(fragment);
    this.index.set(key, fragments);
    if (fragment.parent) {
      const cluster = this.clusters.get(fragment.parent) || [];
      cluster.push(fragment);
      this.clusters.set(fragment.parent, cluster);
    }
  }

  public match(text: string): TextFragment[] {
    const tokens = getSearchTokens(text).filter(token => token.value !== 'xxx');
    if (!tokens.length) {
      return [];
    }
    // Find most likely OCR parent box.
    const parents = new Map<string, number>();
    for (const token of tokens) {
      const fragments = this.index.get(token.value);
      if (!fragments) {
        continue;
      }
      for (const fragment of fragments) {
        if (fragment.parent) {
          parents.set(fragment.parent, 1 + (parents.get(fragment.parent) || 0));
        }
      }
    }
    let bestParent: string = '';
    let bestCount: number = 0;
    for (const [parent, count] of parents.entries()) {
      if (count > bestCount) {
        bestCount = count;
        bestParent = parent;
      }
    }
    if (bestCount < this.getMinCount(tokens.length)) {
      return [];
    }
    return this.clusters.get(bestParent) || [];
  }

  private getMinCount(tokens: number): number {
    if (tokens <= 3) {
      return tokens;
    }
    if (tokens <= 6) {
      return tokens - 1;
    }
    if (tokens <= 9) {
      return tokens - 2;
    }
    return 8;
  }
}
